/**
 * First-party analytics tracker (Marveland pattern).
 * - pageview on every route change
 * - conversion on WhatsApp / tel: clicks (the site's money actions)
 * Zero cookies: a random client id lives in localStorage. Events go to the
 * CMS worker via sendBeacon; failures are silent by design.
 * Prerender-safe: everything runs client-side only.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { CMS_API_BASE } from './cms-api';

function cid(): string {
  let id = '';
  try {
    id = localStorage.getItem('gl_cid') || '';
    if (!id) {
      id = `c_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem('gl_cid', id);
    }
  } catch {
    id = 'anon';
  }
  return id;
}

/** 防重複上報：同一轉化簽名 30 秒內只發一次（連點／重複 handler 保護） */
const recentSigs = new Map<string, number>();
function conversionAllowed(sig: string): boolean {
  const now = Date.now();
  for (const [k, t] of recentSigs) if (now - t > 30000) recentSigs.delete(k);
  if (recentSigs.has(sig)) return false;
  recentSigs.set(sig, now);
  return true;
}

export function track(type: 'pageview' | 'conversion', name?: string, dest?: string): void {
  try {
    // 後台自身流量不計入——團隊操作會污染訪客數據
    if (window.location.pathname.startsWith('/admin')) return;
    const params = new URLSearchParams(window.location.search);
    if (type === 'conversion') {
      const sig = `${name || 'cta'}|${dest || ''}|${window.location.pathname}`;
      if (!conversionAllowed(sig)) return;
    }
    const payload = {
      type,
      name,
      dest,
      path: window.location.pathname,
      ref: document.referrer ? document.referrer.slice(0, 300) : '',
      cid: cid(),
      src: params.get('utm_source') || '',
      med: params.get('utm_medium') || '',
      camp: params.get('utm_campaign') || '',
      lang: navigator.language || '',
      dev: window.matchMedia('(max-width: 768px)').matches ? 'm' : 'd',
    };
    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
    navigator.sendBeacon(`${CMS_API_BASE}/api/track`, blob);
  } catch {
    /* analytics must never break the page */
  }
}

/** Mount once inside the Router: pageviews + money-action click listener.
 *  /admin 自身流量不入數據（團隊操作 ≠ 訪客行為）。 */
export function useTracker(): void {
  const { pathname } = useLocation();

  useEffect(() => {
    if (pathname.startsWith('/admin')) return;
    track('pageview');
  }, [pathname]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const el = (e.target as HTMLElement | null)?.closest?.(
        'a[href*="wa.me"], a[href^="tel:"], a[href^="/contact"]'
      ) as HTMLAnchorElement | null;
      if (!el) return;
      const href = el.getAttribute('href') || '';
      if (href.includes('wa.me')) track('conversion', 'whatsapp_click', href);
      else if (href.startsWith('tel:')) track('conversion', 'tel_click', href);
      else if (href === '/contact' && pathname !== '/contact') track('conversion', 'contact_nav', href);
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
